import React from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { DocumentType } from '@marageti/z4-sdk/lib/documents/Document';
import { Link } from 'react-router-dom';
import ConfirmDelete from '../../../components/confirm-delete';
import DocumentList from './document-list';

type DocsViewProps = {
  docs: Doc[];
  docError: ApiError | undefined;
  isLoading: boolean;
  itineraries: Doc[];
  docToDelete: Doc | null;
  handleDelete: (doc: Doc) => void;
  onCancelDelete: () => void;
  onClickDocument: (document: Doc) => void;
  onConfirmDelete: () => void;
  submissionError: ApiError | undefined;
};

const DocsView = ({
  docs,
  docError,
  isLoading,
  itineraries,
  docToDelete,
  handleDelete,
  onCancelDelete,
  onClickDocument,
  onConfirmDelete,
  submissionError,
}: DocsViewProps) => (
  <Container
    maxWidth="md"
    sx={{
      paddingTop: {
        desktop: 5,
        mobile: 4,
      },
      paddingBottom: {
        desktop: 5,
        mobile: 4.5,
      },
    }}
  >
    <Stack spacing={4}>
      {docError && (
      <Alert severity="error">
        <AlertTitle>There was an issue downloading the document</AlertTitle>
        {docError.message}
        {docError.code && `Code: ${docError.code} `}
        {docError.id && `ID: ${docError.id}`}
      </Alert>
      )}
      {/* General Documents */}
      <Stack
        direction={{ xs: 'column', md: 'row' }} // 'xs' denotes mobile and 'md' and above denotes desktop
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 4 }}
      >
        <Typography variant="h2">
          Documents
        </Typography>
        <Button
          data-e2e="ztest-trip-match-docs-upload"
          variant="outlined"
          component={Link}
          to="general-document"
          sx={{
            minWidth: { xs: '100%', md: 'fit-content' },
            mt: { xs: 2, md: 0 }, // Add margin-top for mobile only
          }}
        >
          Upload Document
        </Button>
      </Stack>
      <DocumentList
        documents={docs}
        isLoading={isLoading}
        onClickDocument={onClickDocument}
        onClickDelete={handleDelete}
        emptyList={(
          <Typography variant="body1" sx={{ mb: 4 }}>
            There are no documents attached to this trip.
          </Typography>
      )}
      />

      {/* Itinerary Documents */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 4 }}
      >
        <Typography variant="h2">
          Itineraries
        </Typography>
        <Button
          data-e2e="ztest-trip-match-docs-upload"
          variant="outlined"
          component={Link}
          to="sample-itinerary"
          sx={{
            minWidth: { xs: '100%', md: 'fit-content' },
            mt: { xs: 2, md: 0 },
          }}
        >
          Upload Itinerary
        </Button>
      </Stack>
      <DocumentList
        documents={itineraries}
        isLoading={isLoading}
        onClickDocument={onClickDocument}
        onClickDelete={handleDelete}
        emptyList={(
          <Typography variant="body1" sx={{ mb: 4 }}>
            There are no itineraries attached to this trip.
          </Typography>
      )}
      />
    </Stack>
    {docToDelete && (
      <ConfirmDelete
        onCancel={onCancelDelete}
        onRemove={onConfirmDelete}
        open={!!docToDelete}
        submissionError={submissionError}
        title={`Remove ${docToDelete?.documentType === DocumentType.DocumentGeneral ? 'Document' : 'Itinerary'}?`}
        helperText={`The ${docToDelete?.documentType === DocumentType.DocumentGeneral ? 'document' : 'itinerary'} will be permanently deleted and the traveler will no longer have access.`}
      />
    )}
  </Container>
);

export default DocsView;
